import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle, Clock, RefreshCw, Image } from "lucide-react";
import { groundOpsApi } from "@/api";

const SEVERITY_STYLE = {
  critical: "bg-red-500/10 border-red-500/30 text-red-400",
  high:     "bg-orange-500/10 border-orange-500/30 text-orange-400",
  medium:   "bg-amber-500/10 border-amber-500/30 text-amber-400",
  low:      "bg-slate-700/30 border-slate-600/30 text-slate-400",
};

const STATUS_STYLE = {
  open:     "text-red-400",
  resolved: "text-green-400",
  closed:   "text-slate-500",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60)   return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export default function RampReports({ user }) {
  const [reports, setReports]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState("all");   // all | open | resolved
  const [expanded, setExpanded] = useState(null);

  function load() {
    setLoading(true);
    groundOpsApi.listIncidents(user.station)
      .then(setReports)
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [user.station]);

  const filtered = reports.filter(r =>
    filter === "all" ? true : r.status === filter
  );

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-black text-white">Incident Reports</h2>
          <p className="text-[11px] text-slate-500">{reports.length} total · {user.station}</p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-orange-400 transition border border-slate-700/40 rounded-lg px-2.5 py-1.5"
          data-testid="refresh-reports-btn"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { key: "all",      label: "All",      count: reports.length },
          { key: "open",     label: "Open",     count: reports.filter(r => r.status === "open").length },
          { key: "resolved", label: "Resolved", count: reports.filter(r => r.status === "resolved").length },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-bold border transition-all
              ${filter === tab.key ? "bg-orange-500/15 border-orange-500/30 text-orange-400" : "bg-[#0c1929] border-slate-700/30 text-slate-500"}`}
            data-testid={`filter-${tab.key}`}
          >
            {tab.label}
            <span className={`px-1 rounded text-[8px] font-black ${filter === tab.key ? "bg-orange-500/20" : "bg-slate-700/50"}`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-center py-10 text-slate-600 animate-pulse text-sm">Loading reports...</div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 text-slate-600">
          <CheckCircle size={36} className="mx-auto mb-3 opacity-20" />
          <p className="text-sm">No {filter !== "all" ? filter : ""} reports at {user.station}</p>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map(r => {
          const isOpen = expanded === r.id;
          return (
            <div
              key={r.id}
              className={`rounded-2xl border overflow-hidden transition-all cursor-pointer ${SEVERITY_STYLE[r.severity] || SEVERITY_STYLE.medium}`}
              onClick={() => setExpanded(isOpen ? null : r.id)}
              data-testid={`report-${r.id?.slice(0, 8)}`}
            >
              {/* Summary row */}
              <div className="flex items-start gap-3 p-4">
                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm text-white">{r.incident_type}</span>
                    <span className={`text-[8px] font-black uppercase border px-1.5 py-0.5 rounded ${SEVERITY_STYLE[r.severity]}`}>
                      {r.severity}
                    </span>
                    <span className={`text-[9px] font-bold ml-auto ${STATUS_STYLE[r.status] || "text-slate-500"}`}>
                      {r.status?.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-2">
                    <span>{r.station}</span>
                    <span>·</span>
                    <span>By {r.reported_by}</span>
                    <span>·</span>
                    <Clock size={8} />
                    <span>{timeAgo(r.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Expanded detail */}
              {isOpen && (
                <div className="px-4 pb-4 border-t border-white/5 bg-black/20">
                  <p className="text-[12px] text-slate-300 mt-3 leading-relaxed">{r.description}</p>

                  {/* Photos */}
                  {r.photo_urls && r.photo_urls.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[9px] text-slate-500 uppercase tracking-widest font-bold mb-2 flex items-center gap-1">
                        <Image size={9} /> Photo Evidence ({r.photo_urls.length})
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {r.photo_urls.map((src, idx) => (
                          <a
                            key={idx}
                            href={src}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-24 h-24 rounded-xl overflow-hidden border border-orange-500/30 hover:border-orange-400 transition"
                            data-testid={`report-photo-${idx}`}
                          >
                            <img src={src} alt={`evidence ${idx + 1}`} className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {r.flight_id && (
                    <div className="mt-2 text-[10px] text-slate-500">Flight ref: {r.flight_id}</div>
                  )}
                  <div className="mt-2 text-[9px] text-slate-600">
                    Reported: {new Date(r.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
